<template>
  <div v-tooltip="dropdownTooltip" data-testid="insights-actions-dropdown">
    <sf-dropdown
      :dropdown-id="dropdownId"
      :is-open="isDropdownOpen"
      :is-disabled="isDropdownDisabled"
      :variant="dropdownVariant"
      drawer-direction="right"
      @dropdown-clicked="toggleDropdown"
      @dropdown-closed="closeDropdown"
    >
      <!-- Trigger Button -->
      <template #trigger>
        <div class="sf-level">
          <span>{{ actionsLabel }}</span>
          <div v-if="isActionRunning" class="sf-spinner tw-border-gray-200" />
        </div>
      </template>

      <!-- Dropdown Menu -->
      <template #menu>
        <sf-dropdown-item-header>
          <div>{{ runActionLabel }}</div>
        </sf-dropdown-item-header>

        <!-- Archive workflows -->
        <sf-dropdown-item
          v-if="!isLite && isAdmin"
          :is-tall="true"
          data-testid="insights-bulk-actions-archive-workflows"
          @click="archiveWorkflowModal"
        >
          <div>{{ I18n.t('companies_overview.actions.archive_workflow') }}</div>
        </sf-dropdown-item>

        <!-- Download as Excel -->
        <sf-dropdown-item
          :is-tall="true"
          data-testid="insights-download-as-excel"
          :class="handleDownloadExcelClass"
          @click="handleDownloadExcelClick"
        >
          <div v-tooltip="downloadAsExcelTooltip">
            <div :class="downloadAsExcelClasses">
              {{ downloadAsExcelTitle }}
            </div>
          </div>
        </sf-dropdown-item>

        <!-- Send a Message -->
        <sf-dropdown-item
          v-if="!isLite"
          v-tooltip.placementLeft.text="periodNotSelectedTooltip"
          :is-tall="true"
          :is-disabled="!hasPeriodSelection"
          data-testid="insights-bulk-actions-send-message"
          @click="handleSendMessageClick"
        >
          <div>{{ I18n.t('companies_overview.actions.send_a_message') }}</div>
        </sf-dropdown-item>

        <!-- Running Actions -->
        <template v-if="isActionRunning">
          <sf-dropdown-item-header>
            <div>
              {{ I18n.t('companies_overview.actions.actions_in_progress') }}
            </div>
          </sf-dropdown-item-header>

          <sf-dropdown-item
            v-for="action in activeActions"
            :key="action.insight_action_run_id"
            @click="handleRunningActionClick(action)"
          >
            <span class="sf-nowrap">{{
              actionDisplayName(action.action_name)
            }}</span>
            <span class="sf-spinner sf-spinner-primary tw-mx-2"></span>
            <strong>{{ action.processed_count }}</strong
            >/<span>{{ action.total_count }}</span>
          </sf-dropdown-item>
        </template>
      </template>
    </sf-dropdown>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { useStore } from 'vuex'
import { storeToRefs } from 'pinia'
import I18n from 'i18n-js'

import { useDropdown } from '@app/shared/pinia/dropdown'
import { dropdowns } from '@app/shared/types/dropdown'
import { useBulkActionsStore } from '../../store/bulk-actions'
import { Job } from '../../types/index'
import {
  SfDropdown,
  SfDropdownItem,
  SfDropdownItemHeader,
} from '@app/shared/vue/components/sf-dropdown'

import {
  CREATE_REMARK_ACTION,
  DOWNLOAD_AS_EXCEL_ACTION,
  CREATE_REMARK_ACTIONS,
  CREATE_REMARK_ON_COMPANY_ACTION,
  ARCHIVE_WORKFLOW_ACTION,
} from './constants'

// ---------- Store & Pinia Setup ----------
const store = useStore()
const bulkActionsStore = useBulkActionsStore()

const dropdownStore = useDropdown()
const { currentDropdownId } = storeToRefs(dropdownStore)
const isLite = inject('isLite')

// ---------- Props & Emits ----------
const emit = defineEmits(['actionSelected'])

// ---------- Computed Properties ----------
const selectedFileCount = computed(
  () => store.state.companies.selectedCompanies.size
)
const isLoading = computed(() => !store.getters.allCompaniesProcessed)
const hasPeriodSelection = computed(() => store.state.periodSelection)
const isDropdownDisabled = computed(
  () => selectedFileCount.value === 0 || isLoading.value
)
const dropdownVariant = computed(() =>
  isDropdownDisabled.value ? 'neutral' : 'primary'
)
const isActionRunning = computed(() => bulkActionsStore.isActionRunning)
const activeActions = computed(() => bulkActionsStore.actionsInProgress)
const dropdownId = dropdowns.INSIGHTS_ACTIONS
const isDropdownOpen = computed(() => currentDropdownId.value === dropdownId)
const periodNotSelectedTooltip = computed(() =>
  hasPeriodSelection.value
    ? ''
    : I18n.t('companies_overview.actions.select_a_period_to_run_action')
)

// ---------- Labels & i18n ----------
const actionsLabel = I18n.t('report/template.sidebar.actions')
const companiesFullyLoaded = computed(
  () => store.state.companies.companiesFullyLoaded
)

const downloadAsExcelTitle = I18n.t(
  'companies_overview.progress_v2.download_as_xlsx'
)
const downloadAsExcelClasses = computed(() =>
  companiesFullyLoaded.value ? '' : 'sf-disabled'
)
const downloadAsExcelTooltip = computed(() =>
  companiesFullyLoaded.value
    ? ''
    : I18n.t('companies_overview.progress_v2.download_as_xlsx_disabled_tooltip')
)

const runActionLabel = computed(() =>
  selectedFileCount.value === 1
    ? I18n.t('companies_overview.progress_v2.run_action_for_one_file')
    : I18n.t('companies_overview.progress_v2.run_action_for_multiple_files', {
        number: selectedFileCount.value,
      })
)

const dropdownTooltip = computed(() => {
  if (isLoading.value)
    return I18n.t('companies_overview.progress_v2.tooltip_loading')
  if (selectedFileCount.value === 0)
    return I18n.t('companies_overview.progress_v2.tooltip_no_files')
  return ''
})

const handleDownloadExcelClass = computed(() =>
  !companiesFullyLoaded.value ? '!tw-cursor-not-allowed' : ''
)

// ---------- Methods ----------
const toggleDropdown = () => dropdownStore.toggleDropdown(dropdownId)
const closeDropdown = () => dropdownStore.closeDropdown()

const handleSendMessageClick = (payloadOverride = {}) => {
  emit('actionSelected', {
    action: CREATE_REMARK_ACTION,
    payload: {
      message: '',
      type: 'note',
      readOnly: false,
      selectedTemplateId: null,
      actionName: CREATE_REMARK_ON_COMPANY_ACTION,
      tagIds: [],
      ...payloadOverride,
    },
  })
  closeDropdown()
}

const handleDownloadExcelClick = () => {
  if (!companiesFullyLoaded.value) {
    return
  }

  emit('actionSelected', { action: DOWNLOAD_AS_EXCEL_ACTION })
  closeDropdown()
}

const handleArchiveWorkflowClick = (payloadOverride = {}) => {
  emit('actionSelected', {
    action: ARCHIVE_WORKFLOW_ACTION,
    payload: {
      readOnly: false,
      selectedWorkflowId: null,
      ...payloadOverride,
    },
  })
  closeDropdown()
}

const actionDisplayName = (actionName: string | undefined) => {
  if (actionName) {
    return CREATE_REMARK_ACTIONS.includes(actionName)
      ? I18n.t('companies_overview.actions.send_a_message')
      : actionName
  }
}

const handleRunningActionClick = (action: Job) => {
  if (
    action.action_name &&
    CREATE_REMARK_ACTIONS.includes(action.action_name)
  ) {
    const payload = {
      message: action.payload.text,
      type: action.payload.type,
      selectedTemplateId: action.payload.template_ids,
      tagIds: action.payload.tag_ids,
      readOnly: true,
      actionName: action.action_name,
    }
    handleSendMessageClick(payload)
  }
  if (action.action_name === ARCHIVE_WORKFLOW_ACTION) {
    const payload = {
      selectedWorkflowId: action.payload.firm_workflow_id,
      readOnly: true,
    }
    handleArchiveWorkflowClick(payload)
  }
}

const archiveWorkflowModal = () => {
  emit('actionSelected', {
    action: ARCHIVE_WORKFLOW_ACTION,
    payload: {
      actionName: ARCHIVE_WORKFLOW_ACTION,
    },
  })
  dropdownStore.closeDropdown()
}

const isAdmin = computed(() => {
  return window.Silverfin.isCurrentUserAdmin()
})
</script>
